/** Содержимое выдвижной панели для прокладки труб. */
import {
	CATALOG,
	WaterMedium,
	getCatalogItem,
	isWaterPoint,
	waterMediumColor,
	waterMediumLabel,
} from "@/shared/catalog";
import { CatalogSectionKind, pipedEndIds } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { DrawerSection, Tool, useEditorStore } from "@/widgets/editor";

export const WaterDrawer = () => {
	const tool = useEditorStore((s) => s.tool);
	const setTool = useEditorStore((s) => s.setTool);
	const pipeSettings = useEditorStore((s) => s.pipeSettings);
	const setPipeSettings = useEditorStore((s) => s.setPipeSettings);
	const setActiveCatalog = useEditorStore((s) => s.setActiveCatalog);
	const setDrawer = useEditorStore((s) => s.setDrawer);
	const setPlacing = useEditorStore((s) => s.setPlacing);
	const plumbingLayer = useProjectStore((s) => s.project.layers.plumbing);
	const pipesLayer = useProjectStore((s) => s.project.layers.pipes);
	const waterPoints = Object.values(plumbingLayer.fixtures).filter((fixture) =>
		isWaterPoint(getCatalogItem(fixture.ref)),
	);
	const piped = pipedEndIds(pipesLayer.items);
	const unpiped = waterPoints.filter((fixture) => !piped.has(fixture.id)).length;
	const pipeCount = Object.keys(pipesLayer.items).length;

	return (
		<div className="contents">
			<UIKit.Button
				type="button"
				title="Труба (P)"
				className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm${tool === Tool.Pipe ? " border-accent bg-accent-soft font-medium text-accent" : " border-border bg-surface text-ink hover:border-accent/50 hover:bg-bg"}`}
				onClick={(event) => {
					event.currentTarget.blur();
					setTool(Tool.Pipe);
				}}
			>
				<UIKit.IconPipe />
				Проложить трубу
			</UIKit.Button>
			<div className="flex flex-col gap-2">
				<UIKit.Text as="span" className="text-xs font-semibold tracking-label text-ink uppercase">
					Вода
				</UIKit.Text>
				<UIKit.SegmentedControl<WaterMedium>
					value={pipeSettings.medium}
					options={[WaterMedium.Cold, WaterMedium.Hot].map((medium) => ({
						value: medium,
						label: waterMediumLabel(medium),
						icon: (
							<UIKit.Text
								as="span"
								className="size-2.5 rounded-full"
								style={{ background: waterMediumColor(medium) }}
							/>
						),
						title: medium === WaterMedium.Hot ? "Горячее водоснабжение" : "Холодное водоснабжение",
					}))}
					onChange={(medium) => setPipeSettings({ medium })}
				/>
			</div>
			<div className="flex flex-col gap-1 border-t border-border pt-2.5">
				<UIKit.Text as="span" className="text-xs font-semibold tracking-label text-ink uppercase">
					Материал
				</UIKit.Text>
				{CATALOG.pipe.items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						title={item.description}
						className={`flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left text-xs text-ink hover:bg-accent-soft${pipeSettings.material === item.id ? " border-accent bg-accent-soft font-medium text-accent" : ""}`}
						onClick={() => setPipeSettings({ material: item.id })}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
						<UIKit.Text as="span" className="text-xs text-ink tabular-nums">
							Ø{item.diameter}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
			{waterPoints.length === 0 && (
				<UIKit.Button
					type="button"
					className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-left text-xs text-amber-800 hover:bg-amber-50"
					onClick={() => {
						setActiveCatalog(CatalogSectionKind.Water);
						setDrawer(DrawerSection.Fixtures);
						setTool(Tool.Place);
						setPlacing({ catalog: CatalogSectionKind.Water, item: "riser-cold" });
					}}
				>
					<UIKit.IconSocket />
					Сначала поставьте стояки и водорозетки — раздел «Водоснабжение» в каталоге
				</UIKit.Button>
			)}
			<div className="flex flex-col gap-1 rounded-lg bg-bg px-2.5 py-2 text-xs text-ink">
				<UIKit.Text as="span">
					Труб: <b className="text-ink">{pipeCount}</b>
				</UIKit.Text>
				<UIKit.Text as="span">
					Точек водоснабжения: <b className="text-ink">{waterPoints.length}</b>
				</UIKit.Text>
				<UIKit.Text as="span">
					Точек без трубы: <b className={unpiped > 0 ? "text-amber-700" : "text-ink"}>{unpiped}</b>
				</UIKit.Text>
			</div>
			<UIKit.Text className="mt-auto text-xs text-ink">
				Труба начинается кликом по стояку, коллектору или водорозетке и идёт по полу только под
				прямыми углами: клики по плану ставят повороты, клик по другой точке подключает. Подъёмы от
				пола к выводам считаются сами. Горячую трубу к стояку ХВС не подвести — точка должна
				принимать выбранную воду. Alt — без привязки к сетке, Esc — отменить.
			</UIKit.Text>
		</div>
	);
};
